import { type RefCallback, useCallback, useId, useRef, useState } from "react";
import { AlertTriangle, Check } from "lucide-react";
import type { FrameworkResponse } from "@patent-studio/api-types";
import { Button } from "@/components/ui/button";
import { MicroLabel } from "@/components/ui/MicroLabel";
import { Panel } from "@/components/ui/Panel";
import { StatusPill } from "@/components/ui/StatusPill";
import type { Labels } from "./SubsectionRow";
import { unplacedOf, unplacedWords } from "./UnplacedTray";

const CLOCK = new Intl.DateTimeFormat(undefined, {
  hour: "2-digit",
  minute: "2-digit",
});

interface Gaps {
  names: string[];
  figures: boolean;
}

/** What the plan leaves out, named as the attorney reads it: "FIG. 3", "Claim 7". */
function notPlaced(response: FrameworkResponse, labels: Labels): Gaps {
  const items = unplacedOf(response);
  return {
    names: items.map((item) => unplacedWords(item, labels)),
    figures: items.some((item) => item.kind === "figure"),
  };
}

function CoveragePill({
  what,
  placed,
  total,
}: {
  what: string;
  placed: number;
  total: number;
}) {
  const complete = placed === total;
  return (
    <StatusPill
      tone={complete ? "success" : "warning"}
      size="sm"
      icon={
        complete ? (
          <Check aria-hidden="true" />
        ) : (
          <AlertTriangle aria-hidden="true" />
        )
      }
    >
      {`${what} ${String(placed)} of ${String(total)} placed`}
    </StatusPill>
  );
}

function Coverage({
  response,
  gaps,
}: {
  response: FrameworkResponse;
  gaps: Gaps;
}) {
  const { coverage } = response;
  return (
    <div className="flex flex-wrap items-center gap-2">
      <MicroLabel>Coverage</MicroLabel>
      <CoveragePill
        what="Figures"
        placed={coverage.figures_placed}
        total={coverage.figures_total}
      />
      <CoveragePill
        what="Independent claims"
        placed={coverage.claims_placed}
        total={coverage.claims_total}
      />
      {gaps.names.length > 0 && (
        <span className="text-warning-strong">{`Not placed: ${gaps.names.join(", ")}`}</span>
      )}
    </div>
  );
}

function Confirmed({
  at,
  held,
  land,
  onEdit,
}: {
  at: string | null | undefined;
  held: boolean;
  land: RefCallback<HTMLElement>;
  onEdit: () => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-3">
      <StatusPill tone="success" icon={<Check aria-hidden="true" />}>
        {at === null || at === undefined
          ? "Confirmed"
          : `Confirmed ${CLOCK.format(Date.parse(at))}`}
      </StatusPill>
      <span className="flex-1">
        This structure is saved exactly as shown for the decision record.
      </span>
      <Button
        ref={land}
        variant="secondary"
        size="dense"
        disabled={held}
        onClick={onEdit}
      >
        Edit framework
      </Button>
    </div>
  );
}

/** The question confirming with a gap asks, and the rule it names when a figure is missing. */
function GapQuestion({
  gaps,
  confirming,
  land,
  onKeepEditing,
  onConfirmAnyway,
}: {
  gaps: Gaps;
  confirming: boolean;
  land: RefCallback<HTMLElement>;
  onKeepEditing: () => void;
  onConfirmAnyway: () => void;
}) {
  const questionId = useId();
  const reasonId = useId();
  return (
    <Panel
      tone="warning"
      pad="snug"
      role="alertdialog"
      aria-labelledby={questionId}
      aria-describedby={reasonId}
      className="space-y-2"
    >
      <p
        id={questionId}
        className="font-semibold"
      >{`${gaps.names.join(", ")} ${gaps.names.length === 1 ? "is" : "are"} not placed.`}</p>
      <p id={reasonId}>
        {gaps.figures &&
          "37 CFR 1.74 requires the Detailed Description to refer to each view by figure number. "}
        You can confirm anyway and address this in review.
      </p>
      <div className="flex gap-2">
        <Button
          ref={land}
          variant="secondary"
          size="dense"
          onClick={onKeepEditing}
        >
          Keep editing
        </Button>
        <Button
          variant="confirm"
          size="dense"
          disabled={confirming}
          onClick={onConfirmAnyway}
        >
          Confirm anyway
        </Button>
      </div>
    </Panel>
  );
}

/**
 * Focus for the control the card shows after the attorney's own act: the question a press
 * opens, the act it closes back to, Edit framework once confirmed. It waits until a control
 * can take it, since Confirm framework is held closed while a confirmation is in flight.
 */
function useArrival() {
  const arrivingRef = useRef(false);
  const land = useCallback((control: HTMLElement | null) => {
    if (control === null || !arrivingRef.current) return;
    control.focus();
    if (document.activeElement === control) arrivingRef.current = false;
  }, []);
  return {
    arrive: () => {
      arrivingRef.current = true;
    },
    land,
  };
}

/**
 * Coverage and the yes. Confirming with a figure or claim not placed asks first, naming them,
 * and says what 37 CFR 1.74 requires when a figure is missing; the attorney may confirm anyway.
 * Confirmed, the card says so with the time and offers to edit again.
 */
export function CommitCard({
  response,
  labels,
  editing,
  confirming,
  held,
  onConfirm,
  onEdit,
}: {
  response: FrameworkResponse;
  labels: Labels;
  editing: boolean;
  confirming: boolean;
  /** Praxis is proposing, so the yes and Edit framework wait until its run lands. */
  held: boolean;
  onConfirm: () => void;
  onEdit: () => void;
}) {
  const [asking, setAsking] = useState(false);
  const { arrive, land } = useArrival();
  const gaps = notPlaced(response, labels);
  const confirmed = response.confirmed_current && !editing;
  const empty = (response.framework.sections ?? []).length === 0;
  return (
    <Panel
      shape="card"
      pad="snug"
      outline={confirmed ? "success" : undefined}
      className="mt-6 space-y-3 text-sm"
    >
      <Coverage response={response} gaps={gaps} />
      {confirmed ? (
        <Confirmed
          at={response.framework.confirmed_at}
          held={held}
          land={land}
          onEdit={() => {
            arrive();
            onEdit();
          }}
        />
      ) : asking ? (
        <GapQuestion
          gaps={gaps}
          confirming={confirming || held}
          land={land}
          onKeepEditing={() => {
            arrive();
            setAsking(false);
          }}
          onConfirmAnyway={() => {
            arrive();
            setAsking(false);
            onConfirm();
          }}
        />
      ) : (
        <div className="flex flex-wrap items-center gap-3">
          <span className="flex-1">
            Drafting follows this order and covers every section and subsection.
            Confirm when the structure is right.
          </span>
          <Button
            ref={land}
            variant="confirm"
            disabled={confirming || held || empty}
            onClick={() => {
              arrive();
              if (gaps.names.length > 0) setAsking(true);
              else onConfirm();
            }}
          >
            Confirm framework
          </Button>
        </div>
      )}
    </Panel>
  );
}
